import { parse } from 'wellknown';

import { isMapGeometry, MapGeometry } from 'src/app/models/geometry.model';
import { environment } from 'src/environments/environment';

import { PlantationHistoryItem, RiskConfigVersion } from './plot-history.model';

/**
 * Encode a plantation_code for use in the URL path while PRESERVING "/".
 *
 * Supplier-portal codes can contain slashes (e.g. "3/NewPlot2"); the backend
 * matches them with a FastAPI `:path` converter, so the slashes must survive.
 * We encode each segment individually and rejoin, rather than encoding the
 * whole code (which would turn "/" into "%2F" and break the route).
 */
export function encodePlantationCode(code: string): string {
  return code.split('/').map(encodeURIComponent).join('/');
}

/**
 * History endpoint URL. `crd` + `plantation_table` query params are added
 * automatically by CrdInterceptor, so only the code goes in the path here.
 */
export function buildHistoryUrl(code: string): string {
  return `${environment.CRD_API}v2/plantations/${encodePlantationCode(code)}/history`;
}

/**
 * Config-versions endpoint URL (AGD-367): every risk-config generation for a
 * dataset, resolved through the catalog. Mounted under the v3 router
 * (`/api/v3/datasets/...`) — note this differs from the v2 `/history` endpoint.
 * `crd` is a PATH param here; CrdInterceptor also appends it (and
 * `plantation_table`) as query params, which the endpoint ignores. Fetched once
 * per dataset — the response is ETag-cacheable.
 */
export function buildConfigVersionsUrl(crd: string): string {
  return `${environment.CRD_API}v3/datasets/${encodeURIComponent(crd)}/risk-config-versions`;
}

export interface PlotHistoryFeatureProps {
  version: number | null;
  /** Drives the two-tone fill: latest version = blue, older versions = grey. */
  isLatest: boolean;
}

export interface PlotHistoryFeature {
  type: 'Feature';
  geometry: MapGeometry;
  properties: PlotHistoryFeatureProps;
}

export interface PlotHistoryFeatureCollection {
  type: 'FeatureCollection';
  features: PlotHistoryFeature[];
}

/**
 * Convert one history version's WKT geometry into a GeoJSON Feature for the map.
 * Returns null when the version carries no resolvable geometry (skipped on the map).
 */
export function wktToFeature(
  item: PlantationHistoryItem,
  opts: { isLatest: boolean }
): PlotHistoryFeature | null {
  if (!item.geo) return null;
  let geometry: unknown;
  try {
    geometry = parse(item.geo);
  } catch {
    geometry = null;
  }
  // Unmapped AgriTrace versions may contain POLYGON EMPTY. Keep the version in
  // the timeline, but exclude shapes without usable coordinates from the map.
  if (!isMapGeometry(geometry)) return null;
  return {
    type: 'Feature',
    geometry,
    properties: { version: item.version, isLatest: opts.isLatest }
  };
}

export function toFeatureCollection(
  features: PlotHistoryFeature[]
): PlotHistoryFeatureCollection {
  return { type: 'FeatureCollection', features };
}

/**
 * A risk that was *present* (overlapped) in a given plot version, resolved for
 * display. `label` is ready to render: the display name plus " (layerVersion)"
 * when the version's config generation records one (AGD-368).
 */
export interface PresentRisk {
  name: string;
  display_name: string;
  layer_version: string | null;
  label: string;
}

/**
 * Resolve the risks that OVERLAPPED in one plot version, each with its layer
 * version, for display on that version's row.
 *
 * - "Present" = the version's own `risks[]` entries with `has_overlap` — the
 *   historical overlap result for that version (per AGD-323 the history endpoint
 *   returns these per version, including older ones).
 * - `display_name` + `layer_version` come from the version's OWN config
 *   generation (resolved via its `risk_config_version_id`), keyed by risk name —
 *   so an older version reflects the layer versions it actually ran under.
 * - Fallbacks: if the version is pre-stamp (no generation) or the name isn't in
 *   the generation, fall back to the dataset risk catalog for the display name
 *   and omit the layer version (shown only when present).
 */
export function resolvePresentRisks(
  version: PlantationHistoryItem,
  generation: RiskConfigVersion | null,
  catalogDisplayByName: ReadonlyMap<string, string>
): PresentRisk[] {
  const genByName = new Map(
    (generation?.risks ?? []).map((risk) => [risk.name, risk])
  );
  const result: PresentRisk[] = [];
  for (const risk of version.risks ?? []) {
    if (!risk.has_overlap || !risk.name) {
      continue;
    }
    const resolved = genByName.get(risk.name);
    const displayName =
      resolved?.display_name ??
      catalogDisplayByName.get(risk.name) ??
      risk.name;
    const layerVersion = resolved?.layer_version ?? null;
    result.push({
      name: risk.name,
      display_name: displayName,
      layer_version: layerVersion,
      label: layerVersion ? `${displayName} (${layerVersion})` : displayName
    });
  }
  return result;
}

/**
 * Turn a raw SCD2 `reason` into a row title. Reasons are free-form strings from
 * the backend (e.g. "geometry_updated"), so we just de-snake_case and capitalise
 * — "risk_layer_recalculated" -> "Risk layer recalculated".
 */
export function humanizeReason(reason: string | null): string {
  if (!reason) return '-';
  const text = reason.replace(/_/g, ' ').trim();
  return text.charAt(0).toUpperCase() + text.slice(1);
}

/**
 * Turn a raw `data_source` into a display label: de-snake_case and Title-Case
 * each word — "supplier_portal" -> "Supplier Portal". Returns "-" when absent.
 */
export function humanizeSource(source: string | null): string {
  if (!source) return '-';
  return source
    .replace(/_/g, ' ')
    .split(' ')
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}
